import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { CheckCircleOutline, CancelOutlined, Visibility, VisibilityOff } from '@material-ui/icons';

export const getPasswordChecks = (password = '') => ({
    minLength: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
});

const PasswordWithValidation = ({
    value,
    onChange,
    placeholder,
    name = 'password',
    disabled = false,
    inputClassName = '',
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [focused, setFocused] = useState(false);

    const checks = useMemo(() => getPasswordChecks(value), [value]);

    const checklistItems = [
        { key: 'minLength', label: 'Minimum 8 characters' },
        { key: 'uppercase', label: 'At least one uppercase letter' },
        { key: 'lowercase', label: 'At least one lowercase letter' },
        { key: 'digit', label: 'At least one numeric digit' },
        { key: 'special', label: 'At least one special character' },
    ];

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {focused && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 'calc(100% + 12px)',
                        left: 0,
                        zIndex: 10,
                        minWidth: 300,
                        background: 'var(--brand-surface, #fff)',
                        border: '1px solid var(--brand-border, #d9dee7)',
                        borderRadius: '8px',
                        boxShadow: '0 6px 16px rgba(20, 29, 52, 0.18)',
                        p: '10px 12px',
                        '&:after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-7px',
                            left: '20px',
                            width: 12,
                            height: 12,
                            background: 'var(--brand-surface, #fff)',
                            borderRight: '1px solid var(--brand-border, #d9dee7)',
                            borderBottom: '1px solid var(--brand-border, #d9dee7)',
                            transform: 'rotate(45deg)',
                        }
                    }}
                >
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'var(--brand-text, #1f2937)', mb: 0.9 }}>
                        Password must contain:
                    </Typography>
                    {checklistItems.map((item) => {
                        const passed = checks[item.key];
                        return (
                            <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                                {passed ? (
                                    <CheckCircleOutline style={{ color: '#2e7d32', fontSize: 18 }} />
                                ) : (
                                    <CancelOutlined style={{ color: '#d32f2f', fontSize: 18 }} />
                                )}
                                <Typography sx={{ fontSize: '12px', color: 'var(--brand-text-muted, #6b7280)', lineHeight: 1.2 }}>
                                    {item.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box sx={{ position: 'relative', width: '100%' }}>
                <input
                    className={inputClassName}
                    type={showPassword ? 'text' : 'password'}
                    name={name}
                    autoComplete="new-password"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    disabled={disabled}
                    style={{ paddingRight: '92px' }}
                />
                <IconButton
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowPassword((prev) => !prev)}
                    disabled={disabled}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: '42%',
                        transform: 'translateY(-50%)',
                        borderRadius: '8px',
                        p: 0.35,
                        color: '#4d4d4d',
                    }}
                >
                    {showPassword ? <VisibilityOff style={{ fontSize: 16 }} /> : <Visibility style={{ fontSize: 16 }} />}
                </IconButton>
            </Box>
        </Box>
    );
};

export default PasswordWithValidation;
